import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetSourceDetailsQuery } from '@/services/RoutingManagementService';
import { KeenIcon } from '@/components';
import { formatDisplayDate } from '@/utils/Date';

export const VersionSources: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const versionId = Number(id);
  const navigate = useNavigate();

  const {
    data: sourceDetails,
    isLoading,
    isError,
    error,
  } = useGetSourceDetailsQuery(versionId);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3 text-gray-700 dark:text-gray-200 contrast:text-white animate-pulse">
              <KeenIcon
                icon="spinner"
                className="text-3xl text-blue-600 dark:text-blue-400 contrast:text-blue-400"
                aria-hidden="true"
              />
              <span className="text-xl font-medium">
                Loading source details...
              </span>
            </div>
          </div>
        ) : isError ? (
          <div className="bg-red-50 dark:bg-red-900/20 contrast:bg-red-900/30 border border-red-200 dark:border-red-800 contrast:border-red-700 rounded-xl p-6 shadow-md text-center">
            <p className="text-red-600 dark:text-red-300 contrast:text-red-200 font-semibold text-lg">
              Error loading source details: {error?.toString()}
            </p>
            <button
              type="button"
              onClick={handleBack}
              className="mt-4 inline-flex items-center px-4 py-2 bg-red-500 dark:bg-red-700 contrast:bg-red-700 text-white rounded-lg hover:bg-red-600 dark:hover:bg-red-800 contrast:hover:bg-red-800 transition duration-300"
            >
              <KeenIcon icon="arrow-left" className="mr-2" aria-hidden="true" />{' '}
              Back
            </button>
          </div>
        ) : !sourceDetails ||
          !sourceDetails.source_files ||
          sourceDetails.source_files.length === 0 ? (
          <div className="bg-gray-100 dark:bg-gray-800 contrast:bg-gray-100 border border-gray-200 dark:border-gray-700 contrast:border-gray-700 rounded-xl p-6 shadow-md text-center">
            <p className="text-gray-700 dark:text-gray-200 contrast:text-white font-medium text-lg">
              No source data found.
            </p>
            <button
              type="button"
              onClick={handleBack}
              className="mt-4 inline-flex items-center px-4 py-2 bg-gray-500 dark:bg-gray-700 contrast:bg-gray-700 text-white rounded-lg hover:bg-gray-600 dark:hover:bg-gray-800 contrast:hover:bg-gray-800 transition duration-300"
            >
              <KeenIcon icon="arrow-left" className="mr-2" aria-hidden="true" />{' '}
              Back
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-100 contrast:bg-black rounded-xl shadow-md overflow-hidden">
            <div className="bg-[#234ec5] dark:bg-blue-900 contrast:bg-blue-900 text-white p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="py-2 px-3 bg-white/20 dark:bg-white/20 contrast:bg-white/20 rounded-full hover:bg-white/30 dark:hover:bg-white/30 contrast:hover:bg-white/30 transition duration-300"
                  aria-label="Go back to version listings"
                >
                  <KeenIcon
                    icon="arrow-left"
                    className="text-white"
                    aria-hidden="true"
                  />
                </button>
                <h1 className="text-2xl font-bold">
                  Version {sourceDetails.version} Sources
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <KeenIcon icon="file" className="text-xl" aria-hidden="true" />
                <span className="text-sm font-medium">
                  {sourceDetails.source_file_count} Source Files
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-6 bg-gray-100 p-4 rounded-lg shadow-inner">
                <h2 className="text-lg font-semibold text-gray-800 mb-2">
                  Version Overview
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">
                      Version ID:
                    </span>{' '}
                    {sourceDetails.version_id}
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">Filename:</span>{' '}
                    {sourceDetails.version_filename}
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">
                      Total Sources:
                    </span>{' '}
                    {sourceDetails.source_file_count}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
                {sourceDetails.source_files.map((source) => (
                  <div
                    key={source.id}
                    role="article"
                    className="bg-gray-100 p-4 rounded-lg shadow-md border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-md font-semibold text-gray-800 break-all">
                        {source.geojson_filename || source.excel_filename}
                      </h3>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          source.has_geojson_content
                            ? 'bg-blue-100 dark:bg-blue-900/30 contrast:bg-blue-900 text-blue-800 dark:text-blue-200 contrast:text-blue-100'
                            : 'bg-purple-100 dark:bg-purple-900/30 contrast:bg-purple-900 text-purple-800 dark:text-purple-200 contrast:text-purple-100'
                        }`}
                      >
                        {source.has_geojson_content ? 'GeoJSON' : 'Excel'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2 line-clamp-2">
                      Features: {source.features_count}
                    </p>
                    <div className="text-sm text-gray-700">
                      <p>
                        Uploaded:{' '}
                        {formatDisplayDate(new Date(source.created_at))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VersionSources;
